import { useSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import Settings from '@mui/icons-material/Settings';
import BugReport from '@mui/icons-material/BugReport';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { Link as RouterLink, useNavigate } from 'react-router';
// material
import { alpha } from '@mui/material/styles';
import { Button, Box, Divider, MenuItem, Typography } from '@mui/material';
// routes
import { PATH_ADMIN, PATH_DASHBOARD, PATH_RESEARCH } from '../../routes/paths';
// hooks
import useAuth from '../../hooks/useAuth';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import { isAdminRole } from '../../common/adminRoles';
// components
import { MIconButton } from '../../components/@material-extend';
import MyAvatar from '../../components/MyAvatar';
import MenuPopover from '../../components/MenuPopover';
import useLocales from 'hooks/useLocales';

export default function AccountPopover() {
  const { translate } = useLocales();
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const { user, logout } = useAuth();
  const isAdmin = isAdminRole(user?.registeredRole);
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout?.();
      if (isMountedRef.current) {
        navigate('/');
        handleClose();
      }
    } catch {
      enqueueSnackbar('Unable to logout', { variant: 'error', autoHideDuration: 6000 });
    }
  };

  const MAIN_OPTIONS = [
    {
      label: translate('menu.settings'),
      icon: Settings,
      linkTo: PATH_DASHBOARD.user.account
    },
    {
      label: translate('menu.external_platforms'),
      icon: LanguageOutlinedIcon,
      linkTo: PATH_DASHBOARD.externalPlatforms.root
    },
    {
      label: translate('menu.explore'),
      icon: ExploreOutlinedIcon,
      linkTo: PATH_DASHBOARD.explore.pathways
    },
    {
      label: translate('menu.suggestions'),
      icon: TipsAndUpdatesOutlinedIcon,
      linkTo: PATH_DASHBOARD.explore.suggestions
    },
    {
      label: translate('menu.report_problem'),
      icon: BugReport,
      linkTo: PATH_DASHBOARD.user.report
    },
    ...(isAdmin
      ? [
          {
            label: translate('menu.admin_console'),
            icon: AdminPanelSettingsOutlinedIcon,
            linkTo: PATH_ADMIN.root
          },
          {
            label: translate('menu.research'),
            icon: ScienceOutlinedIcon,
            linkTo: PATH_RESEARCH.root
          }
        ]
      : [])
  ];

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 40,
          height: 40,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72)
            }
          })
        }}
      >
        <MyAvatar sx={{ width: 32, height: 32 }} />
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 220 }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {[user?.firstName, user?.lastName].filter(Boolean).join(' ')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ mt: 1, mb: 0.5 }} />

        {MAIN_OPTIONS.map((option) => (
          <MenuItem
            key={option.label}
            to={option.linkTo}
            component={RouterLink}
            onClick={handleClose}
            sx={{ typography: 'body2', py: 1, px: 2.5 }}
          >
            <Box component={option.icon} sx={{ mr: 2, fontSize: 24 }} />
            {option.label}
          </MenuItem>
        ))}

        <Box sx={{ p: 2, pt: 2.5 }}>
          <Button fullWidth size="small" color="inherit" variant="outlined" onClick={handleLogout}>
            {translate('components.account_popover.logout')}
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
